import React, { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "../../admin/AuthContext";
import { formatApiErrorDetail } from "../../admin/api";
import { ASSETS } from "../../i18n/translations";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";

export default function AdminLogin() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email.trim(), password);
      toast.success("Willkommen zurück.");
      const next = location.state?.from?.pathname || "/admin";
      navigate(next, { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Login fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="admin-login-page" className="min-h-screen flex bg-[#fafbfd]">
      <aside className="hidden lg:flex flex-col justify-between bg-[#1d428a] text-white w-[42%] p-12 relative overflow-hidden">
        <div className="absolute inset-0 opacity-30"
             style={{
               backgroundImage:
                 "linear-gradient(rgba(255,255,255,0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.08) 1px, transparent 1px)",
               backgroundSize: "32px 32px",
             }} />
        <div className="relative">
          <Link to="/" className="inline-flex items-center gap-4">
            <img src={ASSETS.logo} alt="Logo" className="h-16 w-auto bg-white p-1" />
            <div className="leading-tight">
              <div className="font-display-mid text-xl tracking-wide">SCHWEIZER</div>
              <div className="font-mono-tech text-[#df4661] mt-0.5">PLANMANUFAKTUR GMBH</div>
            </div>
          </Link>
          <h1 className="font-display text-5xl md:text-6xl mt-16 leading-[0.95]">
            ADMIN.<br />
            <span className="text-[#df4661]">DASHBOARD.</span>
          </h1>
          <p className="mt-6 text-white/75 max-w-md text-sm leading-relaxed">
            Anmeldung für Verwaltung von Kontaktanfragen, Servicemodellen und Kontaktdaten.
          </p>
        </div>
        <div className="relative font-mono-tech text-white/60">
          MONTIEREN · BAUEN · WARTEN
        </div>
      </aside>

      <main className="flex-1 grid place-items-center p-6 md:p-12">
        <form onSubmit={onSubmit} className="w-full max-w-md" data-testid="admin-login-form">
          <div className="font-mono-tech text-[#1d428a] mb-4">
            <span className="inline-block w-2 h-2 bg-[#df4661] mr-3 align-middle" />
            LOGIN · ADMIN
          </div>
          <h2 className="font-display text-4xl md:text-5xl mb-8 text-[#0A0A0A]">Anmelden.</h2>

          <div className="space-y-px bg-[#1d428a]/15 border border-[#1d428a]/15">
            <div className="bg-white p-5">
              <label className="font-mono-tech text-[#df4661] flex items-center gap-3">
                <span>01</span><span>E-MAIL *</span>
              </label>
              <input
                data-testid="admin-login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                className="mt-3 w-full bg-transparent border-0 border-b border-[#1d428a]/30 focus:border-[#df4661] outline-none py-2 font-display-mid text-lg text-[#0A0A0A]"
              />
            </div>
            <div className="bg-white p-5">
              <label className="font-mono-tech text-[#df4661] flex items-center gap-3">
                <span>02</span><span>PASSWORT *</span>
              </label>
              <input
                data-testid="admin-login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                className="mt-3 w-full bg-transparent border-0 border-b border-[#1d428a]/30 focus:border-[#df4661] outline-none py-2 font-display-mid text-lg text-[#0A0A0A]"
              />
            </div>
          </div>

          <button
            data-testid="admin-login-submit"
            type="submit"
            disabled={loading}
            className="mt-8 inline-flex items-center gap-3 bg-[#1d428a] text-white font-mono-tech px-6 py-4 hover:bg-[#df4661] disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
          >
            <span className="w-1.5 h-1.5 bg-[#df4661]" />
            {loading ? "ANMELDEN…" : "ANMELDEN"}
            <ArrowRight size={15} strokeWidth={2} />
          </button>

          <div className="mt-8 font-mono-tech text-[#5A6270]">
            <Link to="/" className="link-underline hover:text-[#1d428a]">← ZURÜCK ZUR WEBSEITE</Link>
          </div>
        </form>
      </main>
    </div>
  );
}
